/**
 * Auth API Service
 * Hanterar kommunikation med auth-endpoints
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:7071/api';
const REFRESH_CSRF_TOKEN_STORAGE_KEY = 'refreshCsrfToken';

const devLog = {
  error: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.error(...args);
  },
};

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  displayName?: string;
}

// API response wrapper
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message?: string;
  };
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    displayName: string;
    subscriptionTier: 'free' | 'premium';
    onboardingCompleted: boolean;
    settings: {
      language: 'sv' | 'en';
      householdDisplayName?: string;
      notifications: {
        vmaAlerts: boolean;
        checklistReminders: boolean;
        expirationWarnings: boolean;
        weeklyDigest: boolean;
      };
      privacy: {
        shareStatsWithFamily: boolean;
        allowAnonymousAnalytics: boolean;
      };
    };
  };
  accessToken: string;
  refreshToken?: string;
  refreshCsrfToken?: string;
  expiresIn: number;
  tokenType: string;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const encoded = encodeURIComponent(name);
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    if (cookie.startsWith(`${encoded}=`)) {
      return decodeURIComponent(cookie.substring(encoded.length + 1));
    }
  }
  return null;
}

function getRefreshCsrfCookieName(): string {
  return import.meta.env.VITE_REFRESH_CSRF_COOKIE_NAME || 'bh_refresh_csrf';
}

function getStoredRefreshCsrfToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(REFRESH_CSRF_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistRefreshCsrfToken(token?: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      localStorage.setItem(REFRESH_CSRF_TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_CSRF_TOKEN_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

function hydrateRefreshCsrfFromAuthResponse(data: AuthResponse): void {
  if (data.refreshCsrfToken) {
    persistRefreshCsrfToken(data.refreshCsrfToken);
  }
}

export interface AuthError {
  message: string;
  errors?: Array<{ message: string; path: string[] }>;
}

export type RefreshFailureKind = 'retryable' | 'session-ended' | 'user-deleted' | 'blocked';

export class AuthRefreshError extends Error {
  readonly kind: RefreshFailureKind;

  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'AuthRefreshError';

    if (code === 'USER_DELETED') {
      this.kind = 'user-deleted';
    } else if (code === 'USER_BLOCKED' || code === 'IP_BLOCKED') {
      this.kind = 'blocked';
    } else if (
      status === 401
      || code === 'SESSION_EXPIRED'
      || code === 'INVALID_REFRESH_CSRF'
      || code === 'INVALID_REFRESH_TOKEN'
    ) {
      this.kind = 'session-ended';
    } else {
      this.kind = 'retryable';
    }
  }
}

class AuthService {
  private baseUrl: string;
  private refreshPromise: Promise<AuthResponse> | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const url = `${this.baseUrl}/auth/login`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      // Kolla om svaret är tomt
      const text = await response.text();
      if (!text) {
        devLog.error('Empty response from API', { url, status: response.status });
        throw new Error(`Empty response from server (status: ${response.status})`);
      }

      let json: ApiResponse<AuthResponse>;
      try {
        json = JSON.parse(text);
      } catch {
        devLog.error('Invalid JSON response', { url, status: response.status, text });
        throw new Error(`Invalid response from server: ${text.substring(0, 100)}`);
      }

      // Check for IP blocking
      if (response.status === 403 && json.error?.code === 'IP_BLOCKED') {
        const errorMsg = json.error?.message || 'Din IP-adress har blockerats.';
        throw new Error(`IP_BLOCKED: ${errorMsg}`);
      }

      if (!response.ok || !json.success || !json.data) {
        throw new Error(json.error?.message || 'Login failed');
      }

      hydrateRefreshCsrfFromAuthResponse(json.data);

      return json.data;
    } catch (error) {
      devLog.error('Login error:', error);
      throw error;
    }
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const json = await response.json() as ApiResponse<AuthResponse>;

    if (!response.ok || !json.success || !json.data) {
      throw new Error(json.error?.message || 'Registration failed');
    }

    hydrateRefreshCsrfFromAuthResponse(json.data);

    return json.data;
  }

  async refreshToken(refreshToken?: string): Promise<AuthResponse> {
    const csrfToken = getCookie(getRefreshCsrfCookieName()) || getStoredRefreshCsrfToken();
    const response = await fetch(`${this.baseUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });

    const json = await response.json().catch(() => null) as ApiResponse<AuthResponse> | null;

    if (!response.ok || !json?.success || !json.data) {
      const code = json?.error?.code;
      const detail = json?.error?.message || `Token refresh failed (HTTP ${response.status})`;
      const message = code === 'IP_BLOCKED' || code === 'USER_BLOCKED'
        ? `${code}: ${detail}`
        : detail;
      throw new AuthRefreshError(message, response.status, code);
    }

    hydrateRefreshCsrfFromAuthResponse(json.data);

    return json.data;
  }

  refreshTokenWithRetry(refreshToken?: string): Promise<AuthResponse> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      const delays = [1000, 5000, 15000];

      for (let attempt = 0; ; attempt += 1) {
        try {
          return await this.refreshToken(refreshToken);
        } catch (error) {
          const retryable = error instanceof TypeError
            || (error instanceof AuthRefreshError && error.kind === 'retryable');
          const delay = delays[attempt];
          if (!retryable || delay === undefined) {
            throw error;
          }
          const jitteredDelay = Math.floor(delay * (0.5 + Math.random()));
          await new Promise((resolve) => setTimeout(resolve, jitteredDelay));
        }
      }
    })();

    return this.refreshPromise.finally(() => {
      this.refreshPromise = null;
    });
  }

  async getOAuthUrl(
    provider: 'google' | 'microsoft',
    options?: { prompt?: string }
  ): Promise<{ authorizationUrl: string; state: string }> {
    // Use a single callback path without provider. Backend knows which provider
    // is used because we call `/auth/oauth/{provider}/authorize`.
    // Use VITE_APP_URL if available (for React Native WebView), otherwise fall back to window.location.origin
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUri = `${appUrl}/auth/callback`;
    const params = new URLSearchParams({ redirect_uri: redirectUri });
    if (options?.prompt) params.set('prompt', options.prompt);
    const response = await fetch(`${this.baseUrl}/auth/oauth/${provider}/authorize?${params.toString()}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to get ${provider} OAuth URL: ${response.status} ${response.statusText}`);
    }

    const text = await response.text();
    if (!text) {
      throw new Error(`Empty response from ${provider} OAuth endpoint`);
    }

    let json: ApiResponse<{ authorizationUrl: string; state: string }>;
    try {
      json = JSON.parse(text);
    } catch (err) {
      throw new Error(`Invalid JSON response from ${provider} OAuth endpoint: ${text.substring(0, 100)}`);
    }

    if (!json.success || !json.data) {
      throw new Error(`Failed to get ${provider} OAuth URL: ${json.error || 'Unknown error'}`);
    }

    return json.data;
  }

  /**
   * Sign the user out. For Microsoft B2C / Entra External Identities we redirect
   * the browser to the provider's end session endpoint so the user's session
   * at the identity provider is cleared.
   */
  signOut(idToken?: string): void {
    // For Microsoft B2C/CIAM we expect the tenant/policy and optional auth host
    // to be configured via environment variables. Construct an end_session URL
    // that includes a post_logout_redirect_uri back to the app root.
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const postLogout = `${appUrl}/?logged_out=true`;

    const b2cTenant = import.meta.env.VITE_MICROSOFT_B2C_TENANT || '';
    const b2cPolicy = import.meta.env.VITE_MICROSOFT_B2C_POLICY || import.meta.env.VITE_MICROSOFT_B2C_USER_FLOW || '';
    const authHost = import.meta.env.VITE_MICROSOFT_B2C_AUTH_HOST || '';

    if (b2cTenant && b2cPolicy) {
      // CIAM pattern uses {tenant}.{authHost}/{tenantId} and requires tenant id
      if (authHost === 'ciamlogin.com') {
        const tenantId = import.meta.env.VITE_MICROSOFT_B2C_TENANT_ID || '';
        if (tenantId) {
          let endSession = `https://${b2cTenant}.${authHost}/${tenantId}/oauth2/v2.0/logout?p=${encodeURIComponent(
            b2cPolicy
          )}&post_logout_redirect_uri=${encodeURIComponent(postLogout)}`;
          // Add id_token_hint if available (required for automatic logout)
          if (idToken) {
            endSession += `&id_token_hint=${encodeURIComponent(idToken)}`;
          }
          window.location.assign(endSession);
          return;
        }
      }

      // Default B2C pattern
      const tenantDomain = import.meta.env.VITE_MICROSOFT_B2C_TENANT_DOMAIN || `${b2cTenant}.onmicrosoft.com`;
      let endSession = `https://${authHost || 'login.microsoftonline.com'}/${tenantDomain}/oauth2/v2.0/logout?p=${encodeURIComponent(
        b2cPolicy
      )}&post_logout_redirect_uri=${encodeURIComponent(postLogout)}`;
      // Add id_token_hint if available (required for automatic logout)
      if (idToken) {
        endSession += `&id_token_hint=${encodeURIComponent(idToken)}`;
      }
      window.location.assign(endSession);
      return;
    }

    // Fallback: clear local tokens and redirect to app root
    window.location.assign(postLogout);
  }

  async handleOAuthCallback(
    provider: 'google' | 'microsoft',
    code: string
  ): Promise<AuthResponse> {
    // Use same redirect_uri as in getOAuthUrl
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUri = `${appUrl}/auth/callback`;
    const response = await fetch(`${this.baseUrl}/auth/oauth/${provider}/callback`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, redirectUri }),
    });

    const json = await response.json() as ApiResponse<AuthResponse>;

    if (!response.ok || !json.success || !json.data) {
      throw new Error(json.error?.message || 'OAuth login failed');
    }

    hydrateRefreshCsrfFromAuthResponse(json.data);

    return json.data;
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
      });
    } catch {
      // ignore logout endpoint errors (best effort)
    }

    persistRefreshCsrfToken();
  }
}

export const authService = new AuthService();
export { getStoredRefreshCsrfToken };
